import type { CSSProperties, FunctionComponent, HTMLAttributes, MouseEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Logger } from '@pp/core/src/logger/Logger';
import { useIsLegacyDesktop } from '@pp/core/src/styles/hooks/legacyMedia';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';
import { IconButton, IconButtonSize, Icons } from '@pp/core/src/components/controls/iconButton/IconButton';
import { useLegacyContext } from '@pp/core/src/components/legacyMode/LegacyContextProvider';

import styles from './Drawer.module.css';

export interface DrawerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open: boolean;
  className?: string;
  end?: boolean;
  onClose?: (event?: MouseEvent<HTMLElement>) => void;
  title?: ReactElement | string;
  wide?: boolean;
  drawerContentClassName?: string;
  gameStyle?: boolean;
}

export interface DrawerWindowProps extends DrawerProps {
  anchor: { x: number; y: number } | null;
}

const DrawerWindow: FunctionComponent<DrawerWindowProps> = ({
  anchor,
  children,
  className = '',
  end,
  onClose,
  open,
  title,
  wide,
  drawerContentClassName = '',
  gameStyle = false,
  ...rest
}) => {
  const [shouldRender, setShouldRender] = useState(open);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { isLegacyMode } = useLegacyContext();
  const isLegacyDesktop = useIsLegacyDesktop();
  const isSmallButton = gameStyle || (isLegacyMode && isLegacyDesktop);

  const handleClose = (event: MouseEvent<HTMLElement> | undefined): void => {
    if (open) {
      onClose?.(event);
    }
  };

  useEffect(() => {
    if (open) {
      drawerRef.current?.classList.remove(styles.drawer_closing);
      setShouldRender(true);
    } else {
      drawerRef.current?.classList.add(styles.drawer_closing);
    }
  }, [open]);

  const onAnimationEndHandle = (): void => {
    if (!open) {
      setShouldRender(false);
    }
  };

  const drawerWithMods = useModifiers(styles, 'drawer', { end, wide, default: !gameStyle }, { withBaseClass: true });

  if (!shouldRender) {
    return null;
  }

  const root = document.getElementById('drawerRoot');
  if (!root) {
    Logger.logFailure('drawer', {
      additional: 'noRoot',
    });
    return null;
  }

  return createPortal(
    <div
      className={drawerWithMods}
      style={
        {
          '--anchorOffset': anchor ? `translate(${anchor.x}px,${anchor.y}px)` : '',
          '--offset-y': `${anchor?.y ?? 0}px`,
        } as CSSProperties
      }
      ref={drawerRef}
      onAnimationEnd={onAnimationEndHandle}
    >
      <div className={styles.drawer__overlay} onClick={handleClose} />
      <div data-testid="drawer" className={`${styles.drawer__body} ${className}`} {...rest}>
        <div className={styles.drawer__header}>
          <div className={styles.drawer__headerTitle}>{title}</div>
          <div className={styles.drawer__action}>
            <IconButton
              ghost
              icon={Icons.Close}
              onClick={handleClose}
              size={isSmallButton ? IconButtonSize.SMALL : IconButtonSize.TINY}
            />
          </div>
        </div>
        <div className={`${styles.drawer__content} ${drawerContentClassName}`}>{children}</div>
      </div>
    </div>,
    root
  );
};

export const Drawer: FunctionComponent<DrawerProps> = ({ open, end, ...rest }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const updateAnchor = (): void => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        setAnchor({ x: end ? -window.innerWidth + (rect.x + rect.width) : rect.x, y: -window.innerHeight + rect.y });
      }
    };

    const updateAnchorEventCallback = (): void => {
      window.setTimeout(() => {
        updateAnchor();
      });
    };

    updateAnchor();
    window.addEventListener('orientationchange', updateAnchorEventCallback, { passive: true });
    window.addEventListener('resize', updateAnchorEventCallback, { passive: true });

    return (): void => {
      window.removeEventListener('resize', updateAnchorEventCallback);
      window.removeEventListener('orientationchange', updateAnchorEventCallback);
    };
  }, [end, open]);

  return (
    <>
      <DrawerWindow open={open} end={end} anchor={anchor} {...rest} />
      <div className={styles.drawer__anchor} ref={anchorRef} />
    </>
  );
};
